import { useEffect, useRef, useState } from 'react';

import { Transition } from 'history';

import { api } from '@api/reduxApi';
import { useBlocker } from '@hooks/usePrompt';

import { buildUpdatePayload } from './helpers/navigationHelpers';
import { getModalContent, getScreenerValidation } from './helpers/screenerValidation';

interface UseScreenerNavigationBlockingOptions {
  study: Study | undefined;
  screener: Screener;
  shouldBlock?: boolean;
}

export const useScreenerNavigationBlocking = ({
  study,
  screener,
  shouldBlock = false
}: UseScreenerNavigationBlockingOptions) => {
  const [updateStudy] = api.useUpdateStudyMutation();

  const [showScreenerModal, setShowScreenerModal] = useState(false);
  const [blockedTransaction, setBlockedTransaction] = useState<Transition | null>(null);
  const [saving, setSaving] = useState(false);
  const [shouldRetryNavigation, setShouldRetryNavigation] = useState(false);
  const [navigationError, setNavigationError] = useState(false);

  const processingRef = useRef(false);
  const navigationCompleteRef = useRef(false);

  const screenerValidation = getScreenerValidation(screener, study);
  const shouldBlockNavigation = shouldBlock && study && screenerValidation.shouldShowModal && study.has_screener;

  // Block navigation and show modal
  useBlocker((tx) => {
    if (!navigationCompleteRef.current) {
      setBlockedTransaction(tx);
      setShowScreenerModal(true);
    }
  }, shouldBlockNavigation && !navigationCompleteRef.current);

  // Retry blocked navigation
  useEffect(() => {
    if (shouldRetryNavigation && blockedTransaction) {
      blockedTransaction.retry();
      setBlockedTransaction(null);
      setShouldRetryNavigation(false);
      processingRef.current = false;
      navigationCompleteRef.current = false;
    }
  }, [shouldRetryNavigation, blockedTransaction]);

  // Handle navigation error
  useEffect(() => {
    if (navigationError) {
      processingRef.current = false;
      navigationCompleteRef.current = false;
      setNavigationError(false);
    }
  }, [navigationError]);

  const triggerNavigationRetry = () => {
    if (blockedTransaction) {
      setShouldRetryNavigation(true);
    }
  };

  const handleModalCancel = () => {
    setShowScreenerModal(false);
    setBlockedTransaction(null);
  };

  const handleModalContinue = async () => {
    if (!study || saving || processingRef.current) return;

    setSaving(true);
    processingRef.current = true;

    try {
      const updatePayload = buildUpdatePayload(study, screenerValidation.modalType);
      await updateStudy(updatePayload).unwrap();

      setShowScreenerModal(false);
      setSaving(false);

      navigationCompleteRef.current = true;
      triggerNavigationRetry();
    } catch {
      setNavigationError(true);
      setSaving(false);
      setShowScreenerModal(false);
    }
  };

  const currentModalType = screenerValidation.modalType || 'both';
  const modalContent = getModalContent(currentModalType);

  return {
    showScreenerModal,
    modalType: currentModalType,
    isLoading: saving,

    onCancel: handleModalCancel,
    onContinue: handleModalContinue,

    // For banner display
    shouldShowWarning: screenerValidation.shouldShowModal,
    warningTitle: modalContent.title,
    warningMessage: modalContent.message,

    // For modal display
    modalTitle: modalContent.title,
    modalMessage: modalContent.message
  };
};
